// VideoPlayer — novo componente da Onda Companion-Callback
//
// Renderiza video signed URL com HTML5 controls; segue padrao visual de
// ImageDisplay (mesmo wrapper, mesmo Dialog para fullscreen).

import { useState } from 'react';
import { Video as VideoIcon, ExternalLink, Download, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { fmtNumber } from '@/lib/format';

export interface VideoPlayerProps {
  src: string;
  name: string;
  caption?: string;
  bytes?: number;
}

export function VideoPlayer({ src, name, caption, bytes }: VideoPlayerProps) {
  const [open, setOpen] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const sizeMb = bytes ? fmtNumber(bytes / 1024 / 1024, 1) : null;

  if (videoError) {
    return (
      <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg p-3">
        <div className="flex items-center gap-2 text-xs text-amber-900 dark:text-amber-200 mb-1">
          <AlertTriangle className="h-3.5 w-3.5" />
          Video nao carregou. Link bruto:
        </div>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:underline break-all"
        >
          {src}
        </a>
      </div>
    );
  }

  return (
    <>
      <div className="border border-border rounded-lg p-3 bg-muted/30 inline-block max-w-full">
        <div className="flex items-center gap-2 mb-2">
          <VideoIcon className="h-4 w-4 text-rose-500" />
          <span className="text-xs font-medium truncate max-w-[400px]">
            {name}
            {sizeMb && <span className="ml-1 text-muted-foreground">({sizeMb}MB)</span>}
          </span>
        </div>
        <video
          src={src}
          controls
          preload="metadata"
          onError={() => setVideoError(true)}
          className="max-h-60 max-w-full rounded border border-border"
        />
        {caption && (
          <p className="text-xs text-muted-foreground mt-2 max-w-[400px]">{caption}</p>
        )}
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="default" onClick={() => setOpen(true)}>
            <ExternalLink className="h-3.5 w-3.5" />
            Ampliar
          </Button>
          <Button size="sm" variant="default" asChild>
            <a href={src} download={name} target="_blank" rel="noopener noreferrer">
              <Download className="h-3.5 w-3.5" />
              Baixar
            </a>
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-auto p-3">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <VideoIcon className="h-5 w-5 text-rose-500" />
              <span className="truncate">{name}</span>
              <Button size="sm" variant="default" asChild className="ml-auto">
                <a href={src} download={name} target="_blank" rel="noopener noreferrer">
                  <Download className="h-3.5 w-3.5" />
                  Baixar
                </a>
              </Button>
            </DialogTitle>
          </DialogHeader>
          <video src={src} controls autoPlay className="max-w-full mx-auto" />
          {caption && <p className="text-sm text-muted-foreground mt-3 text-center">{caption}</p>}
        </DialogContent>
      </Dialog>
    </>
  );
}
